// @ts-strict-ignore
import * as React from 'react';
import clsx from 'clsx';
import produce from 'immer';
import {Typography} from 'onboarding-components';

import {TextInput} from 'common/onboarding/immersive-onboarding/components/text-input';
import {SvgIconReact} from 'common/components/svg-icon';
import {UserEventPropertiesMapContext} from 'common/onboarding/immersive-onboarding/types';
import {EVENT_INPUT_MAX} from 'common/onboarding/immersive-onboarding/constants';
import {useTranslation} from 'common/i18n/hooks';

import {inputValidator} from '../util';
import styles from './index.module.styl';
import {ConnectorContext} from './connector-context';

type PropertyColumnProps = {
  selectedEvent: string;
  setSelectedProperty: React.Dispatch<React.SetStateAction<string>>;
  skipAnimation?: boolean;
  selectedProperty?: string;
};

const PROPERTY_INPUT_STYLING = {
  height: 50,
  maxWidth: `190px`,
  minWidth: `175px`,
};

/* eslint-disable react/destructuring-assignment */
export const PropertyColumn = ({
  selectedEvent,
  setSelectedProperty,
  skipAnimation = false,
  selectedProperty,
}: PropertyColumnProps) => {
  const {t} = useTranslation();
  const {currentEventPropertiesMap, setCurrentEventPropertiesMap} = React.useContext(UserEventPropertiesMapContext);

  const {setRightRefs, setSelectedRef} = React.useContext(ConnectorContext);
  const [animationComplete, setAnimationComplete] = React.useState(skipAnimation);

  const propertyRefs = React.useRef([]);

  React.useEffect(() => {
    // Create refs as selectedEvent change
    if (currentEventPropertiesMap.has(selectedEvent)) {
      propertyRefs.current = currentEventPropertiesMap.get(selectedEvent).map(() => {
        return React.createRef();
      });
      setRightRefs(propertyRefs.current);

      // If propertyRef is 0, set animation completion right away
      if (!propertyRefs.current.length) {
        setAnimationComplete(true);
      }

      setSelectedRef(null);
    }
  }, [selectedEvent, currentEventPropertiesMap]);

  React.useEffect(() => {
    if (animationComplete === false && skipAnimation === false) {
      propertyRefs.current.forEach((_, index) => {
        setTimeout(() => {
          propertyRefs.current[index].current.style.visibility = `visible`;

          // Set ref to mark animation completion after last element
          if (index === propertyRefs.current.length - 1) {
            setAnimationComplete(true);
          }
        }, 500 * index);
      });
    }
  }, [propertyRefs.current, animationComplete]);

  return (
    <div className={styles.propertiesSelectorContainer}>
      <Typography variant='captionSm' className={styles.propertiesSelectorHeader}>
        {t(`onboarding.events.selected-event-properties`, {selectedEvent})}
      </Typography>
      <ul>
        {currentEventPropertiesMap.get(selectedEvent)?.map((propertyName, propertyIndex) => {
          const ref = propertyRefs.current[propertyIndex];
          return (
            <li
              ref={ref}
              style={{visibility: animationComplete ? `visible` : `hidden`}}
              key={`${selectedEvent}-${propertyName}-${propertyIndex}`}
              className={clsx(styles.eventEditorRow, {[styles.fadeIn]: !animationComplete})}
              onFocus={() => {
                setSelectedProperty(propertyName);
                setSelectedRef(ref);
              }}
            >
              <TextInput
                style={PROPERTY_INPUT_STYLING}
                validator={inputValidator}
                hideValidCheckMark
                defaultValue={propertyName}
                onBlur={(value) => {
                  const updatedPropertyMap = produce(currentEventPropertiesMap, (draft) => {
                    draft.get(selectedEvent)[propertyIndex] = value;
                  });
                  setCurrentEventPropertiesMap(updatedPropertyMap);
                }}
                autoFocus={propertyIndex === 0 && Boolean(selectedProperty)}
              />
              <div
                className={styles.deleteIconWrapper}
                onClick={() => {
                  setCurrentEventPropertiesMap(
                    produce(currentEventPropertiesMap, (draft) => {
                      draft.set(
                        selectedEvent,
                        draft.get(selectedEvent).filter((_, index) => index !== propertyIndex),
                      );
                    }),
                  );
                }}
              >
                <SvgIconReact className={styles.deleteIcon} icon={`trashcan`} />
              </div>
            </li>
          );
        })}
        {animationComplete && currentEventPropertiesMap.get(selectedEvent)?.length < EVENT_INPUT_MAX && (
          <li className={styles.fadeIn} style={{marginRight: `30px`}}>
            <TextInput
              key={currentEventPropertiesMap.get(selectedEvent)?.length ?? Math.random()}
              style={PROPERTY_INPUT_STYLING}
              validator={inputValidator}
              hideValidCheckMark
              placeholder={t(`onboarding.events.add-own-property`)}
              onBlur={(value) => {
                if (inputValidator(value)) {
                  setCurrentEventPropertiesMap(
                    produce(currentEventPropertiesMap, (draft) => {
                      draft.get(selectedEvent).push(value);
                    }),
                  );
                  setSelectedProperty(value);
                }
              }}
            />
          </li>
        )}
      </ul>
    </div>
  );
};
